import React, {useEffect, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useIsMobileViewport} from '../../../hooks/useMediaQuery'

type Option = {value: any; label: string}
type GroupedOption = {label: string; options: Option[]}

export type FilterConfig = {
  key: string
  label: string
  options?: (Option | GroupedOption)[]
  transform?: (value: any) => string
}

export type DateRangeFilter = {
  keyFrom: string
  keyTo: string
  label: string
}

/**
 * Rango de dos valores numericos (min/max). No se puede reusar `DateRangeFilter` porque ese
 * pasa los valores por `formatDate`, que espera un string 'YYYY-MM-DD' y con un numero falla.
 */
export type NumericRangeFilter = {
  keyFrom: string
  keyTo: string
  label: string
}

/** Chips visibles en movil antes de expandir. */
const COLLAPSED_COUNT = 2

interface Props {
  filters: Record<string, any>
  config: FilterConfig[]
  dateRangeFilters?: DateRangeFilter[]
  numericRangeFilters?: NumericRangeFilter[]
  formatDate?: (date: string) => string
  clearFilters?: () => void
  onRemoveFilter?: (key: string | string[]) => void
}

export const AppliedFiltersList: React.FC<Props> = ({
  filters,
  config,
  dateRangeFilters = [],
  numericRangeFilters = [],
  formatDate = (date) => date,
  clearFilters,
  onRemoveFilter,
}) => {
  const intl = useIntl()
  const isMobile = useIsMobileViewport()
  const [expanded, setExpanded] = useState(false)

  // Al volver a escritorio se muestran todos, asi que el estado colapsado no
  // debe quedar pegado si el usuario rota el telefono o agranda la ventana.
  useEffect(() => {
    if (!isMobile) setExpanded(false)
  }, [isMobile])

  const yesLabel = intl.formatMessage({id: 'common.boolean.yes', defaultMessage: 'Yes'})
  const noLabel = intl.formatMessage({id: 'common.boolean.no', defaultMessage: 'No'})
  const removeFilterTitle = (label: string) =>
    intl.formatMessage(
      {id: 'policies.list.filters.removeFilter', defaultMessage: 'Remove {label} filter'},
      {label}
    )
  // Decorative close glyph + field separator — non-translatable; hoisted so they aren't raw JSX literals.
  const removeGlyph = '×'
  const badgeSep = ':'
  // Prettier inserta `{' '}` al reflowar el <strong>, y un literal suelto en JSX
  // rompe `formatjs/no-literal-string-in-jsx`. Se hoistea como const.
  const space = ' '
  const appliedFilters = config
    .map(({key, label, options, transform}) => {
      const value = filters[key]
      // Para valores booleanos, null y undefined son diferentes de false
      if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        return null
      }

      let displayValue: any = value

      // 🔹 Aplanamos opciones si hay grupos (optgroup-like)
      let flattenedOptions: Option[] = []
      if (options) {
        options.forEach((opt) => {
          if ('options' in opt) {
            flattenedOptions.push(...opt.options)
          } else {
            flattenedOptions.push(opt)
          }
        })
      }

      if (Array.isArray(value)) {
        if (options) {
          const labels = value
            .map((v) => flattenedOptions.find((opt) => String(opt.value) === String(v))?.label)
            .filter(Boolean)
          displayValue = labels.length ? labels.join(', ') : value.join(', ')
        } else if (transform) {
          displayValue = value.map((v) => transform(v)).join(', ')
        } else {
          displayValue = value.join(', ')
        }
      } else if (options) {
        // Para valores booleanos, comparar con conversión a string
        const valueStr = typeof value === 'boolean' ? String(value) : String(value)
        const match = flattenedOptions.find((opt) => String(opt.value) === valueStr)
        displayValue = match
          ? match.label
          : typeof value === 'boolean'
          ? value
            ? yesLabel
            : noLabel
          : value
      } else if (transform) {
        displayValue = transform(value)
      } else if (typeof value === 'boolean') {
        displayValue = value ? yesLabel : noLabel
      }

      return (
        <li key={key} className='filter-badge'>
          <span className='filter-content'>
            <strong>
              {label}
              {badgeSep}
            </strong>{space}
            {displayValue}
          </span>
          {onRemoveFilter && (
            <button
              className='btn-remove-filter'
              onClick={() => onRemoveFilter(key)}
              title={removeFilterTitle(label)}
              aria-label={removeFilterTitle(label)}
            >
              {removeGlyph}
            </button>
          )}
        </li>
      )
    })
    .filter(Boolean)

  // Procesar rangos de fechas
  const dateRangeFilterElements = dateRangeFilters
    .map(({keyFrom, keyTo, label}) => {
      const fromValue = filters[keyFrom]
      const toValue = filters[keyTo]

      // Solo mostrar si al menos uno de los valores existe
      if (!fromValue && !toValue) return null

      let displayValue = ''
      if (fromValue && toValue) {
        displayValue = `${formatDate(fromValue)} - ${formatDate(toValue)}`
      } else if (fromValue) {
        displayValue = intl.formatMessage(
          {id: 'policies.list.filters.dateFrom', defaultMessage: 'From {date}'},
          {date: formatDate(fromValue)}
        )
      } else if (toValue) {
        displayValue = intl.formatMessage(
          {id: 'policies.list.filters.dateUntil', defaultMessage: 'Until {date}'},
          {date: formatDate(toValue)}
        )
      }

      return (
        <li key={`${keyFrom}-${keyTo}`} className='filter-badge'>
          <span className='filter-content'>
            <strong>
              {label}
              {badgeSep}
            </strong>{space}
            {displayValue}
          </span>
          {onRemoveFilter && (
            <button
              className='btn-remove-filter'
              onClick={() => {
                // Remover ambos valores del rango pasando un array
                onRemoveFilter([keyFrom, keyTo])
              }}
              title={removeFilterTitle(label)}
              aria-label={removeFilterTitle(label)}
            >
              {removeGlyph}
            </button>
          )}
        </li>
      )
    })
    .filter(Boolean)

  // Rangos numericos: un solo chip por rango, y la X limpia las dos claves.
  const numericRangeFilterElements = numericRangeFilters
    .map(({keyFrom, keyTo, label}) => {
      const fromValue = filters[keyFrom]
      const toValue = filters[keyTo]
      // Se compara contra null/undefined/'' y no por truthy: 0 es un valor valido.
      const hasFrom = fromValue !== undefined && fromValue !== null && fromValue !== ''
      const hasTo = toValue !== undefined && toValue !== null && toValue !== ''
      if (!hasFrom && !hasTo) return null

      let displayValue = ''
      if (hasFrom && hasTo) {
        displayValue = intl.formatMessage(
          {id: 'policies.list.filters.rangeBetween', defaultMessage: '{from} - {to}'},
          {from: fromValue, to: toValue}
        )
      } else if (hasFrom) {
        displayValue = intl.formatMessage(
          {id: 'policies.list.filters.rangeFrom', defaultMessage: 'From {value}'},
          {value: fromValue}
        )
      } else {
        displayValue = intl.formatMessage(
          {id: 'policies.list.filters.rangeUntil', defaultMessage: 'Up to {value}'},
          {value: toValue}
        )
      }

      return (
        <li key={`${keyFrom}-${keyTo}`} className='filter-badge'>
          <span className='filter-content'>
            <strong>
              {label}
              {badgeSep}
            </strong>{space}
            {displayValue}
          </span>
          {onRemoveFilter && (
            <button
              className='btn-remove-filter'
              onClick={() => onRemoveFilter([keyFrom, keyTo])}
              title={removeFilterTitle(label)}
              aria-label={removeFilterTitle(label)}
            >
              {removeGlyph}
            </button>
          )}
        </li>
      )
    })
    .filter(Boolean)

  const allFilters = [...appliedFilters, ...dateRangeFilterElements, ...numericRangeFilterElements]

  if (!allFilters.length) return null

  // En movil los chips ocupan toda la linea, asi que se colapsan a los primeros
  // y el resto queda detras de un toggle. En escritorio siempre se ven todos.
  const isCollapsible = isMobile && allFilters.length > COLLAPSED_COUNT
  const hiddenCount = isCollapsible && !expanded ? allFilters.length - COLLAPSED_COUNT : 0
  const visibleFilters = hiddenCount ? allFilters.slice(0, COLLAPSED_COUNT) : allFilters

  return (
    <div className='filters-applied-container'>
      {clearFilters && (
        <button
          className='btn btn-sm btn-outline-secondary btn-clear-filters'
          onClick={clearFilters}
        >
          <FormattedMessage
            id='policies.list.filters.clearAll'
            defaultMessage='Clear all filters'
          />
        </button>
      )}
      <ul className='filters-applied'>
        {visibleFilters}
        {isCollapsible && (
          <li className='filters-applied__toggle'>
            <button
              type='button'
              className='btn btn-sm btn-link btn-toggle-filters'
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
            >
              {hiddenCount ? (
                <FormattedMessage
                  id='policies.list.filters.showMore'
                  defaultMessage='+{count} more'
                  values={{count: hiddenCount}}
                />
              ) : (
                <FormattedMessage id='policies.list.filters.showLess' defaultMessage='Show less' />
              )}
            </button>
          </li>
        )}
      </ul>
    </div>
  )
}
